/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import * as React from 'react';
import { ICreateActionContext, TelemetryEvent } from '@msdyn365-commerce/core';
import { IncrementalQuantity, Module, Node, ITelemetryContent, getTelemetryAttributes, getPayloadObject, onTelemetryClick } from '@msdyn365-commerce-modules/utilities';
import { IBuyboxData, IBuyboxResources as IBuyboxExtentionResources } from '@msdyn365-commerce-modules/buybox';
import {
	IBuyboxAddToCartViewProps,
	IBuyboxCallbacks,
	IBuyboxKeyInPriceViewProps,
	IBuyboxProductConfigureDropdownViewProps,
	IBuyboxProductConfigureViewProps,
	IBuyboxProductQuantityViewProps,
	IBuyboxState
} from '@msdyn365-commerce-modules/buybox/src/modules/buybox/../../common';
import { IBuyboxViewProps } from '@msdyn365-commerce-modules/buybox/src/modules/buybox/./buybox';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { IBuyboxExtensionData } from './buybox.data';
import { IBuyboxProps as IBuyboxExtentionProps, IBuyboxProps } from '../definition-extensions/buybox.ext.props.autogenerated';
import getPriceAction, { GetGraduatedPriceInput } from '../../../actions/get-graduated-price.action';

// TODO: use correct "React way" instead of hacky/ugly quick solution
const _fetchGraduatedPrice = async (newValue: number, state: IBuyboxState, props: IBuyboxProps<IBuyboxData>) => {
	const productId = state.productPrice?.ProductId || 0;
	const catalogId = state.productPrice?.CatalogId || 0;
	const actionInput = new GetGraduatedPriceInput(newValue, productId, catalogId);

	await getPriceAction(actionInput, props.context.actionContext).then(res => {
		const priceElements = document.querySelectorAll('.msc-price__actual');
		priceElements.forEach(item => item.innerHTML = props.context.cultureFormatter.formatCurrency(res.ProductPrice));
	});
};

/**
 * On Change function.
 * @param callbacks -Callbacks.
 * @returns Update quantity.
 */
const onChangeHandler = (callbacks: IBuyboxCallbacks, context: ICreateActionContext, state: IBuyboxState, props: any) => (newValue: number): boolean => {
	if (callbacks.updateQuantity) {

		// Call API only if user isAuthenticated optherwise the request will fail
		if (context.requestContext.user.isAuthenticated) {
			// Fetch from custom API endpoint to get graduated price
			_fetchGraduatedPrice(newValue, state, props);
		}

		return callbacks.updateQuantity(newValue);
	}
	return true;
};

/**
 * RenderQuantity.
 * @param quantityComponent - QuantityComponent.
 * @param callbacks - Callbacks.
 * @param props - Props.
 * @param state - State.
 * @param extentionResources - ExtentionResources.
 * @param quantityLimitsMessages - QuantityLimitsMessages.
 * @param max - Max.
 * @param telemetryContent - TelemetryContent.
 * @param unitOfMeasure - Unit Of Measure.
 * @returns -- Returns.
 */
const renderQuantity = (
	quantityComponent: IBuyboxProductQuantityViewProps,
	callbacks: IBuyboxCallbacks,
	props: IBuyboxProps<IBuyboxData>,
	state: IBuyboxState,
	extentionResources: IBuyboxExtentionResources,
	quantityLimitsMessages: React.ReactNode,

	// eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
	max: number | undefined,
	telemetryContent?: ITelemetryContent,
	unitOfMeasure?: React.ReactNode
): JSX.Element => {
	// eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
	const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;
	const { resources } = props;
	const { quantity } = state;

	return (
		<Node {...ContainerProps}>
			<Node {...LabelContainerProps}>
				{heading}
				{errors}
			</Node>

			<IncrementalQuantity
				id='ms-buybox__product-quantity-input'
				max={max}
				currentCount={quantity}
				onChange={onChangeHandler(callbacks, props.context.actionContext, state, props)}
				inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
				decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
				incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
				minQuantityText={extentionResources.minQuantityText}
				maxQuantityText={extentionResources.maxQuantityText}
				telemetryContent={telemetryContent}
			/>
			{unitOfMeasure}
			{quantityLimitsMessages}
		</Node>
	);
};

const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData & IBuyboxExtensionData>> = props => {
	const {
		ModuleProps,
		// eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
		MediaGalleryContainerProps,
		// eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
		ProductInfoContainerProps,
		addToCart,
		configure,
		description,
		quantity,
		price,
		title,
		inventoryLabel,
		keyInPrice,
		// eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
		max,
		unitOfMeasure,
		callbacks,
		state,
		resources,
		quantityLimitsMessages,
		telemetryContent,
	} = props;

	const [periodValue, setPeriodValue] = React.useState(resources.monthlyPeriodOption);
	const period = {
		ContainerProps: {},
		heading: <label>{resources.periodLabel}</label>,
		frequencies: (
			<select className='msc-dropdown__select' onChange={event => setPeriodValue(event.target.value)} disabled>
				<option>{periodValue}</option>
			</select>
		),
		disableSubscriptionPeriodDropdown: props.config.disableSubscriptionPeriodDropdown
	};
	let skuText: string = props.resources.skuText;
	skuText += ` ${props.data.product.result?.ItemId}`;

	return (
		<Module {...ModuleProps}>
			<div className='row no-gutters'>
				<div className='col-12 col-sm-6 col-md-4'>
					<div className='ms-buybox__container'>
						<Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
						<Node {...ProductInfoContainerProps}>
							{title}
							{description}
							<Node className='msc-buybox__sku-text' tag='p'>
								{skuText}
							</Node>
							{unitOfMeasure}
							{period && _renderPeriod(period)}
							{configure && _renderConfigure(configure)}
							<Node className='ms-buybox__quantity'>
								{quantity &&
									renderQuantity(
										quantity,
										callbacks,
										props,
										state,
										resources,
										quantityLimitsMessages,
										max,
										telemetryContent,
										unitOfMeasure
									)}
							</Node>
							{inventoryLabel}
							{keyInPrice && _renderKeyInPrice(keyInPrice)}
							{price}
							{addToCart && _renderAddToCart(addToCart, props)}
						</Node>
					</div>
				</div>
				<div className='col-12 col-sm-6 col-md-8'>
					{_renderTextBlock(props.slots && props.slots.textBlocks)}
					{ArrayExtensions.hasElements(props.slots.productSpecification) ? (
						<Node className='msc-buybox__product-specification'>{props.slots.productSpecification[0]}</Node>
					) : null}
				</div>
			</div>
		</Module>
	);
};

/**
 * RenderTextBlock.
 * @param textBlock - textBlock react node array.
 * @returns -- Returns.
 */
const _renderTextBlock = (textBlock: React.ReactNode[]): JSX.Element | undefined => {
	if (!textBlock || textBlock.length === 0) {
		return undefined;
	}

	return (
		<div className='ms-buybox__text-block-container'>
			{textBlock && textBlock.length > 0
				? textBlock.map((textBlock: React.ReactNode, index: number) => {
					return <React.Fragment key={index}>{textBlock}</React.Fragment>;
				})
				: null}
		</div>
	);
};

const _renderAddToCart = (addToCart: IBuyboxAddToCartViewProps, props: any): JSX.Element => {
	const { ContainerProps, errorBlock, button } = addToCart;

	const payLoad = getPayloadObject('click', props.telemetryContent!, 'business-sign-up', '');
	const payload = getPayloadObject(TelemetryEvent.AddToCart, props.telemetryContent!, '', '');
	const attributes = getTelemetryAttributes(props.telemetryContent!, payload);

	let businessSignUpUrl;
	if (props.context.app.routes?.businessPartnerSelection?.destinationUrl) {
		businessSignUpUrl = props.context.app.routes.businessPartnerSelection?.destinationUrl;
	} else {
		businessSignUpUrl = props.context.request.url.requestUrl.origin + (props.context.request?.sitePath ?? '');
	}

	const _registerButton = (
		<a
			title={props.resources.registerLinkText}
			className='msc-cart-icon msc-btn'
			href={businessSignUpUrl}
			aria-label={props.resources.registerLinkText}
			{...attributes}
			onClick={onTelemetryClick(props.telemetryContent!, payLoad, 'business-sign-up')}
		>
			{props.resources.registerLinkText}
		</a>
	);

	const payLoadLink = getPayloadObject('click', props.telemetryContent!, 'sign-in', '');

	const _loginButton = (
		<a
			title={props.resources.loginLinkText}
			className='msc-cart-icon msc-btn'
			href={props.context.request.user.signInUrl}
			aria-label={props.resources.loginLinkText}
			{...attributes}
			onClick={onTelemetryClick(props.telemetryContent!, payLoadLink, 'sign-in')}
		>
			{props.resources.loginLinkText}
		</a>
	);


	return (
		<Node {...ContainerProps}>
			{errorBlock}
			{button}
			{!props.context.request.user.isAuthenticated && (
				<>
					{_registerButton} { ' | '} {_loginButton}
				</>
            )}
		</Node>
	);
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
	const { ContainerProps, dropdowns } = configure;

	return <Node {...ContainerProps}>{dropdowns.map(_renderConfigureDropdown)}</Node>;
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
	const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

	return (
		<Node {...ContainerProps}>
			<Node {...LabelContainerProps}>
				{heading}
				{errors}
			</Node>
			{select}
		</Node>
	);
};

const _renderPeriod = (period: any): JSX.Element | null => {
	const { ContainerProps, LabelContainerProps, heading, frequencies, disableSubscriptionPeriodDropdown } = period;
	const isDisableSubscriptionPeriodDropdown = disableSubscriptionPeriodDropdown;

	if (isDisableSubscriptionPeriodDropdown) {
		return null;
	}
	return (
		<Node {...ContainerProps}>
			<Node {...LabelContainerProps}>{heading}</Node>
			{frequencies}
		</Node>
	);
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
	const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

	return (
		<Node {...ContainerProps}>
			<Node {...LabelContainerProps}>{heading}</Node>
			{input}
		</Node>
	);
};

export default BuyboxView;
